import type {
  ChecklistMergeResponse,
  ChecklistTemplate,
  TemplateDataType,
  TemplateParameters,
} from '../api/templates'

/**
 * What a build or an overlay must ask for (ADR 0010 s5): every {{Token}} the CHOSEN workstreams'
 * text uses, in the order the template declares them, each with the label and the type its owners
 * gave it. A token the text uses and nobody declared is asked for too, as plain text: the API
 * refuses to leave one in, so not asking would only move the refusal to after the click.
 * A PERIOD_ token is never asked for: the API fills it from the day the list is built (the last
 * three full calendar years and the year to date; TemplateTokens.Periods, ruled 2026-09-21).
 */
export interface ParameterField {
  token: string
  label: string
  dataType: TemplateDataType
}

const TOKEN = /\{\{([A-Za-z0-9_]+)\}\}/g

export function tokensIn(text: string | null | undefined): string[] {
  if (!text) return []
  return [...text.matchAll(TOKEN)].flatMap((m) => (m[1] ? [m[1]] : []))
}

/** A token the API fills from the build's own date; the same prefix as TemplateTokens.PeriodPrefix. */
export function isPeriod(token: string): boolean {
  return token.startsWith('PERIOD_')
}

export function parameterFields(
  template: ChecklistTemplate,
  sectionCodes: readonly string[] | null,
): ParameterField[] {
  const used = new Set<string>()
  for (const section of template.sections) {
    if (sectionCodes && !sectionCodes.includes(section.code)) continue
    for (const item of section.items) {
      for (const text of [item.title, item.requestText, item.providerNotes]) {
        for (const token of tokensIn(text)) if (!isPeriod(token)) used.add(token)
      }
    }
  }
  const declared = template.parameters.filter((p) => used.has(p.token))
  const undeclared = [...used].filter((t) => !template.parameters.some((p) => p.token === t))
  return [
    ...declared.map((p) => ({ token: p.token, label: p.label, dataType: p.dataType })),
    ...undeclared.map((t) => ({ token: t, label: t, dataType: 'Text' as const })),
  ]
}

/** What the form can say before the API does. The API checks again: this only saves a round trip. */
export function parameterProblem(field: ParameterField, value: string | undefined): string | null {
  const v = (value ?? '').trim()
  if (v === '') return `${field.label} is needed: the list's text uses it.`
  if (field.dataType === 'Year' && !/^\d{4}$/.test(v)) return `${field.label} is a four-digit year.`
  if (field.dataType === 'Date' && !/^\d{4}-\d{2}-\d{2}$/.test(v))
    return `${field.label} is a date.`
  if (v.includes('{{')) return `${field.label} cannot itself contain a token.`
  return null
}

export function filledParameters(
  fields: readonly ParameterField[],
  values: TemplateParameters,
): TemplateParameters {
  return Object.fromEntries(fields.map((f) => [f.token, (values[f.token] ?? '').trim()]))
}

/** "12 added, 3 updated, 104 unchanged" with only the parts that are not zero. */
export function mergeSummary(result: ChecklistMergeResponse): string {
  const verb = result.committed
    ? { add: 'added', update: 'updated', remove: 'removed' }
    : { add: 'to add', update: 'to update', remove: 'to remove' }
  const parts = [
    result.sectionsCreated > 0
      ? `${result.sectionsCreated} new section${result.sectionsCreated === 1 ? '' : 's'}`
      : null,
    result.added > 0 ? `${result.added} ${verb.add}` : null,
    result.updated > 0 ? `${result.updated} ${verb.update}` : null,
    result.removed > 0 ? `${result.removed} ${verb.remove}` : null,
    result.unchanged > 0 ? `${result.unchanged} unchanged` : null,
    result.errors > 0 ? `${result.errors} in error` : null,
  ].filter((p): p is string => p !== null)
  return parts.length === 0 ? 'Nothing to do.' : parts.join(', ')
}
